<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: $t('messages.usage_information'),
                        href: meta.links.index,
                    },
                    {
                        name: teacher.full_name,
                        href: null,
                    },
                ]"
            />
        </template>

        {{ $tc('messages.experience_points', 2) }}

        <div>
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 md:grid-cols-3">
                <div>
                    <apexchart
                        :type="graphs.pointTypesPieGraph.chart.type"
                        :height="graphs.pointTypesPieGraph.chart.height"
                        :series="graphs.pointTypesPieGraph.series"
                        :options="graphs.pointTypesPieGraph"
                        v-if="graphs.pointTypesPieGraph"
                    >
                    </apexchart>
                </div>
            </div>
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.type')" for="active" />
                        <label class="ml-1 mr-3" v-for="type in options.types" :key="type.value">
                            <input
                                name="type_in[]"
                                type="checkbox"
                                :value="type.value"
                                v-model="form.type_in"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                {{ type.text }}
                            </span>
                        </label>
                        <FormError :value="form.errors.active" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="points.data.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div v-else>
            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.type') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.student') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.comment') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.points', 2) }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.date') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        <span class="sr-only">{{ $t('messages.actions') }}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                    v-for="(currencyPoint, index) in points.data"
                                    :key="currencyPoint.id"
                                >
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        {{ currencyPoint.type_display }}
                                    </td>
                                    <td class="px-6 py-4 text-sm leading-5 text-gray-500">
                                        {{ currencyPoint.student_name }}
                                    </td>
                                    <td class="px-6 py-4 text-sm leading-5 text-gray-500">
                                        {{ currencyPoint.comment }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        <div class="flex space-x-1">
                                            <div>
                                                <BadgePill color="green" v-if="currencyPoint.points">
                                                    {{ currencyPoint.points }}
                                                </BadgePill>
                                            </div>

                                            <div>
                                                <BadgePill color="yellow" v-if="currencyPoint.coins_points">
                                                    {{ currencyPoint.coins_points }}
                                                </BadgePill>
                                            </div>

                                            <div>
                                                <BadgePill color="red" v-if="currencyPoint.health_points">
                                                    {{ currencyPoint.health_points }}
                                                </BadgePill>

                                                <BadgePill
                                                    color="red"
                                                    v-if="currencyPoint.nominal_health_diff != 0"
                                                    class="opacity-50"
                                                    :title="$t('messages.health')"
                                                >
                                                    {{ currencyPoint.nominal_health_diff }}
                                                </BadgePill>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        <span :title="currencyPoint.date_display">
                                            {{ currencyPoint.diff_for_humans }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        <InertiaSquareDeleteButton
                                            v-if="currencyPoint.links.delete && currencyPoint.can.delete"
                                            :href="currencyPoint.links.delete"
                                            :confirm-text="$t('messages.are_you_sure?')"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <template v-slot:pagination>
            <InertiaPagination :paginator="points" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import BadgePill from '@/components/BadgePill.vue';
import EmptyState from '@/components/EmptyState.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        InertiaPagination,
        PanelPage,
        FormLabel,
        FormError,
        BadgePill,
        EmptyState,
        InertiaSquareDeleteButton,
    },
    layout: Layout,
    name: 'Show',
    props: ['points', 'filters', 'options', 'graphs', 'teacher', 'meta'],
    data() {
        return {
            form: this.$inertia.form({
                type_in: this.filters.type_in,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.show);
        },
    },
};
</script>
